/**
 * MSAL / API configuration loaded from Vite env vars at build time.
 *
 * Mirrors docreview's authConfig so the sign-in experience is identical.
 * Fail-closed: missing required env vars throw at module load.
 */
import type { Configuration, RedirectRequest } from '@azure/msal-browser';

function requireEnv(name: string): string {
  const value = import.meta.env[name];
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`Required Vite env var is missing: ${name}. Set it in .env.<mode> before building.`);
  }
  return value;
}

const apiEndpoint = requireEnv('VITE_API_ENDPOINT').replace(/\/+$/, '');
const clientId = requireEnv('VITE_AUTH_CLIENT_ID');
const authority = requireEnv('VITE_AUTH_AUTHORITY');
const scopes = requireEnv('VITE_AUTH_SCOPES')
  .split(',')
  .map((s) => s.trim())
  .filter(Boolean);
const tenantDisplayName = requireEnv('VITE_TENANT_DISPLAY_NAME');

export const apiConfig = {
  apiEndpoint,
  tenantDisplayName,
} as const;

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority,
    knownAuthorities: [new URL(authority).host],
    redirectUri: window.location.origin + '/',
    postLogoutRedirectUri: window.location.origin + '/',
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: { logLevel: 0 },
  },
};

/**
 * Always go through the IdP picker on explicit sign-in / re-auth. Without
 * `prompt: 'select_account'`, External ID for customers will sometimes
 * route federated users (Google) to a local-account password prompt for
 * their synthetic `<objectId>@bacrexternalauth.onmicrosoft.com` UPN —
 * the federated user has no local password, so they're stuck unless they
 * find the small "Sign in with another account" link.
 *
 * select_account always lands on External ID's IdP-picker page, where
 * choosing Google triggers the federation handshake. With Google as the
 * only configured IdP, this is effectively one extra click on the cold
 * sign-in path and silent-token-renewal (`acquireTokenSilent`) is
 * unaffected (it doesn't consult `prompt`).
 *
 * Mirror of the same fix in docreview's authConfig.ts.
 */
export const loginRequest: RedirectRequest = { scopes, prompt: 'select_account' };
export const apiRequest = { scopes } as const;
